import { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import axios from "axios";

import { MEAL_PLAN_GENERATION_API } from "../../../app/constants/constants";
import { Meal } from "../../../components/mealPlanPage/mealPlan.types";
import { DELETE_EXISTING_MEAL_PLAN, INSERT_MEAL_PLAN_WITH_ID, INSERT_MEALS_MANY, INSERT_SHOPPING_LIST_FOR_USER, INSERT_USER_TO_MEAL_PLAN } from "../../../app/graphql/mutations";
import { FETCH_USER_MEAL_PLAN, GET_MEALS_IN_TIMESTAMPS } from "../../../app/graphql/queries";
import { setGroceryListId } from "../../../app/groceryList/groceryList.slice";
import { userIdSelector } from "../../../app/main/mainSlice";
import { setuserMealPlanData, UserMealPlanDataSelector } from "../../../app/mealPlan/mealPlan.slice";
import { useAppDispatch, useAppSelector } from "../../../app/store/hooks";
import useUpdateGroceryList from "../../../hooks/groceryList/useUpdateGroceryList";
import useFetchGroceryListId from "../../../hooks/useFetchGroceryListId";
import useLogRecipeEdit from "../../../hooks/useLogRecipeEdit";
import { ErrorManagementContext } from "../../../utils/errorManagement/ErrorManagementProvider";
import { EventLogsEnum } from "../../../utils/events";
import { formatCustomDate, getDatesFromNow } from "../../../components/mealPlanPage/utils";

const useMealPlanCreation = () => {
  const user = useAppSelector((state) => state.main.user ?? null);
  const userId = useAppSelector(userIdSelector);
  const userMealPlanData = useAppSelector(UserMealPlanDataSelector);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { updateGroceryListFromAddingMeal } = useUpdateGroceryList();
  const { groceryListId } = useFetchGroceryListId();
  const [getMealsInTimeStamp] = useLazyQuery(GET_MEALS_IN_TIMESTAMPS)
  const [addMealToDB] = useMutation(INSERT_MEALS_MANY);
  const [deleteExistingMeals] = useMutation(DELETE_EXISTING_MEAL_PLAN);

  const { cookClubLog } = useContext(ErrorManagementContext);
  const { logRecipeEdit } = useLogRecipeEdit();

  const [addShoppingListForUser] = useMutation(INSERT_SHOPPING_LIST_FOR_USER);
  const [addingMealToDB, setAddingMealToDB] = useState<boolean>(false);
  const [fetchingAiMealPlan, setFetchingAiMealPlan] = useState<boolean>(false);

  const { refetch: refetchUserMeals } = useQuery(FETCH_USER_MEAL_PLAN, {
    variables: {
      userId: user?.id
    },
    skip: !user?.id || userMealPlanData?.meal_plan_id !== null,
    onCompleted: (data) => {
      if (data?.User_To_Meal_Plan[0]) {
        dispatch(setuserMealPlanData(data?.User_To_Meal_Plan[0]))
        const mealPlanIdDB = data?.User_To_Meal_Plan[0].meal_plan_id;
      }
    },
  })

  const refetchMealPlan = async () => {
    const data = await refetchUserMeals();
    if (data?.data?.User_To_Meal_Plan[0]) {
      dispatch(setuserMealPlanData(data?.data?.User_To_Meal_Plan[0]))
    }
  }

  const addGroceryListId = async () => {
    const data = await addShoppingListForUser({
      variables: {
        user_id: user?.id
      }
    })
    const shoppingListId = data?.data?.insert_User_To_Shopping_List_one?.shopping_list_id;
    if (shoppingListId)
      dispatch(setGroceryListId(shoppingListId))
    else {
      console.error("Error generating shopping list id")
    }

    return shoppingListId;
  }

  const addData = async (meal_plan_id_ai: string, meals: any, shoppingListId: string, days: number) => {
    if (meals.length === 0) return;
    const { gteCalculated, lteCalculated } = getDatesFromNow(days);
    gteCalculated.setDate(gteCalculated.getDate() + 1)
    try {

      const { data: { users_by_pk: { Meal_Plans } } } = await getMealsInTimeStamp(
        {
          variables: {
            userId,
            _lte: lteCalculated,
            _gte: gteCalculated,
          }
        }
      )
      const defaultMealPlan = Meal_Plans?.[0] ?? null;
      const mealIds: string[] = defaultMealPlan.Meals.map((meal: Meal) => meal.id) || [];
      try {
        await deleteExistingMeals({
          variables: {
            mealIds
          },
        })

        if (meals.length) {
          const { data: { insert_Meal: { returning: addedMealReturnData } } } = await addMealToDB({
            variables: {
              meals,
            }
          });
          await updateGroceryListFromAddingMeal(addedMealReturnData, shoppingListId);
          logRecipeEdit("ADDED_MEAL", {
            meal_plan_id: meal_plan_id_ai,
            eventData: {
              meals,
            },
          })
        }
      } catch (error) {
        console.error("Error while adding data:", error);
      } finally {
        history.replace('/meal-plan');
      }
    }catch(e){
      console.error("ADDING MEAL FAILED",e)
    }
  }

  const generateMealPlanWithAi = async (inputValue: string, days: number) => {
    try {
      setFetchingAiMealPlan(true);
      const mealPlanGenerationResponse: any = await axios.post(MEAL_PLAN_GENERATION_API, {
        userId,
        message: {
          days,
          prompt: inputValue,
          timezone: formatCustomDate(new Date())
        },
      }).catch((err) => {
        cookClubLog(` Try again`,
          "Error creating meal plan!", {}, { ui: { type: "error" }, browserConsole: true })
        throw new Error('Error generating meal plan', err);
      })
      setFetchingAiMealPlan(false);
      return mealPlanGenerationResponse;

    } catch (err) {
      setFetchingAiMealPlan(false);
      console.error(err)
      return null;
    }
  }

  const generateMealPlan = async (inputValue: string, days: number) => {
    setAddingMealToDB(true);
    try {
      const mealPlan = await generateMealPlanWithAi(inputValue, days).catch((err) => {
        throw new Error('Error generating meal plan', err)
      })
      const meals = mealPlan?.data?.response || [];
      const mealPlanIdAi = meals?.[0]?.meal_plan_id;
      let shoppingListId;
      if (!groceryListId) shoppingListId = await addGroceryListId();
      await addData(mealPlanIdAi, meals, shoppingListId, days);
      logRecipeEdit(EventLogsEnum.ADDED_PLAN_TO_MEAL_PLAN, {
        meal_plan_id: mealPlanIdAi,
        eventData: {
          response_recieved: meals,
        },
      })
      await refetchMealPlan();
    } catch (err) {
      console.error(err)
    } finally {
      setAddingMealToDB(false);
    }
  }

  return { generateMealPlan, loading: addingMealToDB || fetchingAiMealPlan };
}

export default useMealPlanCreation;